import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { campaignApi, scanUrl, merchantApi, auth } from '../../services/api';
import QrModal from './QrModal';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';

export default function CampaignList() {
  const { merchantId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [merchant, setMerchant] = useState(null);
  const [bloggerCfg, setBloggerCfg] = useState(null); // 博主码弹窗 { postType, auto }
  const [savingBlogger, setSavingBlogger] = useState(false);
  const [qr, setQr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingGoogleUrl, setEditingGoogleUrl] = useState(null); // { id, storeUrl, reviewUrl }
  const [savingGoogleUrl, setSavingGoogleUrl] = useState(false);
  // 「探店活动」tab 于 2026-09-19 下掉，这里只剩扫码活动一种，tab 固定住 ——
  // 老链接上带着 ?tab=tandian 也不会再切过去。
  const tab = 'scan';
  const [mSettings, setMSettings] = useState(null); // 商家设置弹窗

  const merchantName = state?.merchantName || '商家';
  const isSuper = auth.isSuperadmin();

  const load = () => {
    setLoading(true);
    campaignApi.list(merchantId).then(setCampaigns).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    merchantApi.get(merchantId).then(setMerchant).catch(() => {});
  }, [merchantId]);

  const openBlogger = () => setBloggerCfg({
    postType: merchant?.bloggerPostType || 'review',
    auto: !!merchant?.bloggerAutoPublish,
  });

  const saveBlogger = async () => {
    setSavingBlogger(true);
    try {
      const m = await merchantApi.update(merchantId, {
        bloggerPostType: bloggerCfg.postType,
        bloggerAutoPublish: bloggerCfg.auto,
      });
      setMerchant(m);
      setBloggerCfg(null);
    } finally {
      setSavingBlogger(false);
    }
  };

  const handleSaveGoogleUrl = async () => {
    if (!editingGoogleUrl) return;
    setSavingGoogleUrl(true);
    try {
      await campaignApi.update(editingGoogleUrl.id, {
        googleStoreUrl:  editingGoogleUrl.storeUrl,
        googleReviewUrl: editingGoogleUrl.reviewUrl,
      });
      setEditingGoogleUrl(null);
      load();
    } finally {
      setSavingGoogleUrl(false);
    }
  };

  const openMerchantSettings = () => setMSettings({
    tandianBalance: merchant?.tandianBalance ?? 0,
    tandianUrgent: !!merchant?.tandianUrgent,
    googleStoreUrl: merchant?.googleStoreUrl || '',
    googleReviewUrl: merchant?.googleReviewUrl || '',
  });
  const saveMSettings = async () => {
    const m = await merchantApi.update(merchantId, {
      tandianBalance: Number(mSettings.tandianBalance) || 0,
      tandianUrgent: !!mSettings.tandianUrgent,
      googleStoreUrl: mSettings.googleStoreUrl,
      googleReviewUrl: mSettings.googleReviewUrl,
    });
    setMerchant(m);
    setMSettings(null);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await campaignApi.create({ merchantId, merchantName, name: newName.trim() });
      setNewName('');
      setCreating(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <AdminHeader
        title={merchantName}
        titleExtra={isSuper && (
          <button style={styles.mSettingsBtn} onClick={openMerchantSettings} title="商家设置">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5-3.414.586.586-3.414 8.5-8.5z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        )}
        back={true}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostBtn small onClick={openBlogger}>博主码</GhostBtn>
            {isSuper && tab === 'scan' && <PrimaryBtn onClick={() => setCreating(true)}>新建活动</PrimaryBtn>}
          </div>
        }
      />

      <div style={styles.body}>
        {/* 「探店活动」整块删了（2026-09-19）。
            探店不再由运营在网站上建活动：商家在 App 里跟助理说要找博主，
            条件谈齐之后 ensureActivitiesForReadyTerms() 自己建一个内部容器（source:'agent'）
            并开始匹配。运营在这儿点「新建探店活动」建出来的是 source:'ops' 的招募贴，
            会贴到全体博主的「可报名活动」列表里 —— 那是上一版产品的做法。
            店铺、单子、待办统一在 /admin/agent 看。 */}
        <>
        {creating && (
          <div style={styles.createCard}>
            <p style={styles.createLabel}>活动名称</p>
            <input
              style={styles.input}
              placeholder="输入活动名称..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
            <div style={styles.createActions}>
              <GhostBtn onClick={() => { setCreating(false); setNewName(''); }}>取消</GhostBtn>
              <PrimaryBtn onClick={handleCreate} disabled={saving || !newName.trim()}>
                {saving ? '创建中...' : '确认创建'}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {loading ? (
          <div style={styles.skeletons}>
            {[1, 2, 3].map(i => <div key={i} style={styles.skeleton} />)}
          </div>
        ) : campaigns.length === 0 ? (
          <EmptyState text={isSuper ? '点击右上角新建活动' : '该商家暂无活动，请联系管理员创建'} />
        ) : (
          <div style={styles.list}>
            {campaigns.map((c) => (
              <div key={c._id} style={styles.card}
                onClick={() => navigate(`/admin/campaign/${c._id}`, { state: { campaignName: c.name } })}>
                <div style={styles.cardTop}>
                  <span style={styles.cardName}>{c.name}</span>
                  <span style={styles.statusDot(c.status)} />
                </div>
                <div style={styles.statsRow}>
                  <div style={styles.stat}>
                    <span style={styles.statNum}>{c.articleTotal || 0}</span>
                    <span style={styles.statLabel}>总文章</span>
                  </div>
                  <div style={styles.statDivider} />
                  <div style={styles.stat}>
                    <span style={styles.statNum}>{c.articleUnscanned || 0}</span>
                    <span style={styles.statLabel}>未扫文章</span>
                  </div>
                  <div style={styles.statDivider} />
                  <div style={styles.stat}>
                    <span style={{ ...styles.statNum, color: '#FA1C51' }}>{c.totalScanCount || 0}</span>
                    <span style={styles.statLabel}>总扫码</span>
                  </div>
                </div>
                <div style={styles.cardFooter}>
                  <div style={styles.qrBtns}>
                    <button
                      style={styles.qrBtnXhs}
                      onClick={(e) => { e.stopPropagation(); setQr({ url: scanUrl.campaign(c._id), title: `${c.name} — 小红书` }); }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/></svg>
                      小红书码
                    </button>
                    <button
                      style={styles.qrBtnGoogle}
                      onClick={(e) => { e.stopPropagation(); setQr({ url: scanUrl.campaignGoogle(c._id), title: `${c.name} — Google Review` }); }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/></svg>
                      Google 码
                    </button>
                    <button
                      style={styles.editGoogleBtn}
                      onClick={(e) => { e.stopPropagation(); setEditingGoogleUrl({ id: c._id, storeUrl: c.googleStoreUrl || '', reviewUrl: c.googleReviewUrl || '' }); }}
                      title="设置 Google Review 链接"
                    >
                      <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5-3.414.586.586-3.414 8.5-8.5z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                  <div style={styles.enterHint}>
                    <span>进入管理</span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 10.5L8.5 7L5 3.5" stroke="#FA1C51" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
      </div>

      {editingGoogleUrl && (
        <div style={styles.overlay} onClick={() => setEditingGoogleUrl(null)}>
          <div style={styles.editModal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.editModalTitle}>Google 链接设置</p>
            <p style={styles.editModalLabel}>店铺链接（分享链接即可）</p>
            <input
              style={styles.editModalInput}
              value={editingGoogleUrl.storeUrl}
              onChange={(e) => setEditingGoogleUrl(u => ({ ...u, storeUrl: e.target.value }))}
              placeholder="maps.app.goo.gl/xxx"
              autoFocus
            />
            <p style={styles.editModalLabel}>Review 直达链接（选填，优先使用）</p>
            <input
              style={styles.editModalInput}
              value={editingGoogleUrl.reviewUrl}
              onChange={(e) => setEditingGoogleUrl(u => ({ ...u, reviewUrl: e.target.value }))}
              placeholder="g.page/r/.../review"
            />
            <div style={styles.editModalBtns}>
              <GhostBtn onClick={() => setEditingGoogleUrl(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={handleSaveGoogleUrl} disabled={savingGoogleUrl}>
                {savingGoogleUrl ? '保存中...' : '保存'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}
      {bloggerCfg && (
        <div style={styles.overlay} onClick={() => setBloggerCfg(null)}>
          <div style={styles.editModal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.editModalTitle}>店铺博主码</p>
            <p style={styles.bloggerNote}>
              博主扫这个码，登录后自己写内容发小红书，同时投稿到本店铺（券宝宝 store_id：{merchant?.qbbStoreId || merchant?.externalId || '—'}）。
            </p>
            <p style={styles.editModalLabel}>默认去向</p>
            <div style={styles.typeRow}>
              <button style={{ ...styles.typeBtn, ...(bloggerCfg.postType === 'review' ? styles.typeBtnOn : {}) }}
                onClick={() => setBloggerCfg(c => ({ ...c, postType: 'review' }))}>店铺评价</button>
              <button style={{ ...styles.typeBtn, ...(bloggerCfg.postType === 'post' ? styles.typeBtnOn : {}) }}
                onClick={() => setBloggerCfg(c => ({ ...c, postType: 'post' }))}>帖子</button>
            </div>
            <label style={styles.autoRow}>
              <input type="checkbox" checked={bloggerCfg.auto} onChange={(e) => setBloggerCfg(c => ({ ...c, auto: e.target.checked }))} />
              <span>提交后自动发到券宝宝（不勾选则暂存后台手动发）</span>
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <GhostBtn onClick={() => setQr({ url: scanUrl.merchantBlogger(merchantId), title: `${merchantName} — 博主码` })}>出二维码</GhostBtn>
              <GhostBtn onClick={() => navigate('/admin/blogger-posts', { state: { merchantId, merchantName } })}>查看投稿</GhostBtn>
            </div>
            <div style={styles.editModalBtns}>
              <GhostBtn onClick={() => setBloggerCfg(null)}>关闭</GhostBtn>
              <PrimaryBtn onClick={saveBlogger} disabled={savingBlogger}>{savingBlogger ? '保存中...' : '保存设置'}</PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {mSettings && (
        <div style={styles.overlay} onClick={() => setMSettings(null)}>
          <div style={styles.editModal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.editModalTitle}>商家设置</p>
            <p style={styles.editModalLabel}>博主探店余额（还剩多少个名额，选人时扣减）</p>
            <input style={styles.editModalInput} type="tel" inputMode="numeric" value={mSettings.tandianBalance} onChange={(e) => setMSettings(u => ({ ...u, tandianBalance: e.target.value.replace(/\D/g, '') }))} placeholder="0" autoFocus />
            <label style={styles.mUrgentRow}>
              <input type="checkbox" checked={!!mSettings.tandianUrgent} onChange={(e) => setMSettings(u => ({ ...u, tandianUrgent: e.target.checked }))} />
              <span>标记「加急找博主」（商家列表置顶 + 急标记）</span>
            </label>
            <p style={styles.editModalLabel}>Google 店铺链接</p>
            <input style={styles.editModalInput} value={mSettings.googleStoreUrl} onChange={(e) => setMSettings(u => ({ ...u, googleStoreUrl: e.target.value }))} placeholder="maps.app.goo.gl/xxx" />
            <p style={styles.editModalLabel}>Google Review 直达链接（选填）</p>
            <input style={styles.editModalInput} value={mSettings.googleReviewUrl} onChange={(e) => setMSettings(u => ({ ...u, googleReviewUrl: e.target.value }))} placeholder="g.page/r/.../review" />
            <p style={styles.editModalHint}>余额与加急即时生效；Google 链接会同步到该商家旗下所有活动。</p>
            <div style={styles.editModalBtns}>
              <GhostBtn onClick={() => setMSettings(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={saveMSettings}>保存</PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {qr && <QrModal url={qr.url} title={qr.title} onClose={() => setQr(null)} />}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  tabs: { display: 'flex', gap: 8, marginBottom: 12 },
  tab: { flex: 1, padding: '9px 0', background: '#fff', color: '#6b7280', border: '1px solid #eee', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  tabOn: { background: '#FA1C51', color: '#fff', borderColor: '#FA1C51', fontWeight: 700 },
  mSettingsBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center' },
  mUrgentRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', margin: '0 0 12px' },
  body: { flex: 1, padding: '12px 12px 24px' },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 120, borderRadius: 12, background: '#efefef' },

  createCard: {
    background: '#fff', borderRadius: 12, padding: '16px',
    marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  createLabel: { fontSize: 13, fontWeight: 600, color: '#374151', margin: '0 0 8px' },
  input: {
    width: '100%', padding: '11px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', color: '#111',
  },
  createActions: { display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' },
  typeRow: { display: 'flex', gap: 8 },
  typeBtn: {
    flex: 1, padding: '10px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8,
    background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer',
  },
  typeBtnOn: { borderColor: '#FA1C51', color: '#FA1C51', background: '#fff7f8', fontWeight: 600 },
  bloggerNote: { fontSize: 12, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.6 },
  bloggerCfg: { marginTop: 14, paddingTop: 14, borderTop: '1px dashed #eee' },
  autoRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#374151' },
  badge: {
    fontSize: 11, color: '#FA1C51', background: '#fff7f8', border: '1px solid #ffd6de',
    borderRadius: 4, padding: '1px 6px', marginRight: 8, flexShrink: 0,
  },
  blankHint: { fontSize: 13, color: '#9ca3af', background: '#f9fafb', borderRadius: 8, padding: '12px', marginBottom: 12 },

  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: '#fff', borderRadius: 12, padding: '16px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)', cursor: 'pointer',
  },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  cardName: { fontSize: 16, fontWeight: 700, color: '#111', flex: 1, marginRight: 8 },
  statusDot: (status) => ({
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
    background: status === 'active' ? '#22c55e' : status === 'paused' ? '#f59e0b' : '#d1d5db',
  }),
  statsRow: { display: 'flex', alignItems: 'center', background: '#f9fafb', borderRadius: 8, padding: '10px 0', marginBottom: 12 },
  stat: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  statNum: { fontSize: 20, fontWeight: 700, color: '#111' },
  statLabel: { fontSize: 11, color: '#9ca3af' },
  statDivider: { width: 1, height: 28, background: '#e5e7eb' },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  qrBtns: { display: 'flex', gap: 6, alignItems: 'center' },
  qrBtnXhs: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '5px 10px', border: 'none', borderRadius: 6,
    background: '#FA1C51', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  qrBtnGoogle: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '5px 10px', border: 'none', borderRadius: 6,
    background: '#4285f4', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  editGoogleBtn: {
    width: 28, height: 28, border: '1px solid #e5e7eb', borderRadius: 6,
    background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  enterHint: { display: 'flex', alignItems: 'center', gap: 3, fontSize: 13, color: '#FA1C51', fontWeight: 600, flexShrink: 0 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  editModal: {
    background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 360,
  },
  editModalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 12px' },
  editModalLabel: { fontSize: 12, color: '#6b7280', margin: '0 0 5px', fontWeight: 500 },
  editModalInput: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 6,
  },
  editModalHint: { fontSize: 11, color: '#9ca3af', margin: '0 0 14px' },
  editModalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
};
